import useSettings from "features/settings/useSettings";
import { useWebSocket } from "features/websoket/WebSocketProvider";
import { size } from "lodash";
import moment, { Moment } from "moment";
import { useEffect, useRef, useState } from "react";
import {
  getStreetSalesData,
  groupByStartTime,
  StreetSalesData,
} from "./streetSalesApi";

export default function useStreetSalesList() {
  const _storageStartDate = sessionStorage.getItem(`street.startDate`);
  const _storageEndDate = sessionStorage.getItem(`street.endDate`);
  const [range, setRange] = useState<
    { start?: Moment; end?: Moment } | undefined
  >({
    start: !!_storageStartDate
      ? moment(_storageStartDate).startOf("day")
      : moment().startOf("day"),
    end: !!_storageEndDate
      ? moment(_storageEndDate).endOf("day")
      : moment().endOf("day"),
  });

  const [settings, setSettings] = useSettings({ key: "streetSales" });
  const [loading, setLoading] = useState<boolean>(true);
  const loaded = useRef(false);
  const [streetSalesData, setStreetSalesData] = useState<StreetSalesData>({});
  const { subscribe, unsubscribe } = useWebSocket();

  const handleFilter = async (
    startDate: string | undefined,
    endDate: string | undefined,
    boatIds: number[] | null,
    programIds: number[] | null,
    silent?: boolean
  ) => {
    if (!silent) {
      setLoading(true);
    }
    if (startDate) {
      sessionStorage.setItem(`street.startDate`, startDate);
    } else {
      sessionStorage.removeItem(`street.startDate`);
    }
    if (endDate) {
      sessionStorage.setItem(`street.endDate`, endDate);
    } else {
      sessionStorage.removeItem(`street.endDate`);
    }
    const { success, data } = await getStreetSalesData(
      startDate,
      endDate,
      boatIds,
      programIds
    );
    loaded.current = true;
    if (success) {
      setStreetSalesData(groupByStartTime(data) || {});
    }
    if (!silent) {
      setLoading(false);
    }
  };
  function load(silent?: boolean) {
    return handleFilter(
      range?.start?.format("YYYY-MM-DD"),
      range?.end?.format("YYYY-MM-DD"),
      settings.boatIds,
      settings.programIds,
      silent
    );
  }
  const isEmpty = loaded.current && size(streetSalesData) === 0;

  useEffect(() => {
    load();
  }, [range, settings.boatIds, settings.programIds]);

  const loader = useRef<(silent?: boolean) => void>();
  loader.current = load;
  useEffect(() => {
    if (range?.start && range?.end) {
      subscribe(
        "streetsales",
        range?.start?.format("YYYY-MM-DD"),
        range?.end?.format("YYYY-MM-DD"),
        dates => {
          loader.current && loader.current(true);
        }
      );
    }
  }, [range]);

  useEffect(() => {
    return () => unsubscribe("streetsales");
  }, []);

  return {
    streetSalesData,
    loading,
    load,
    handleFilter,
    setRange,
    range,
    loaded,
    isEmpty,
    setSettings,
  };
}
